import * as React from "react";
import { useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import {
  blue,
  cyan,
  deepOrange,
  green,
  lightBlue,
  lightGreen,
  lime,
  orange,
  teal,
  yellow
} from "@mui/material/colors";

import { useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/auth-slice";
import { getStaffColor } from "features/scheduling/utils/scheduling.utils";
import { StaffMember } from "services/types/staff.type";

import { SelectedStaffMember } from "./staff-filter.component";

export const STAFF_COLORS = [
  blue,
  green,
  orange,
  cyan,
  teal,
  lightBlue,
  lightGreen,
  yellow,
  lime,
  deepOrange
];

type StaffSearchProps = {
  staff: StaffMember[];
  selectedStaff: Map<number, SelectedStaffMember>;
  onChangeStaff: (value: Map<number, SelectedStaffMember>) => void;
};

export default function StaffSearch({
  staff,
  selectedStaff,
  onChangeStaff
}: StaffSearchProps) {
  const [selectedCounter, setSelectedCounter] = useState<number>(1);
  const currentUser = useAppSelector(selectCurrentUser);
  const handleChange = React.useCallback(
    (values: StaffMember[]) => {
      let added = 0;
      const newSelection = new Map<number, SelectedStaffMember>();
      values.forEach((selectedStaffMember) => {
        if (currentUser?.id !== selectedStaffMember.id) {
          let verifiedSelection = selectedStaff.get(selectedStaffMember.id);
          if (verifiedSelection === undefined) {
            verifiedSelection = {
              ...selectedStaffMember,
              checked: true,
              ...getStaffColor(selectedCounter + added)
            };
            added++;
          }

          newSelection.set(selectedStaffMember.id, verifiedSelection);
        }
      });
      const currentUserSelection = staff.find(
        ({ id }) => currentUser.id === id
      );
      if (currentUserSelection) {
        let verifiedSelection = selectedStaff.get(currentUser.id);
        if (verifiedSelection === undefined) {
          verifiedSelection = {
            ...currentUserSelection,
            checked: true,
            ...getStaffColor(selectedCounter + added)
          };
          added++;
        }
        newSelection.set(currentUser.id, verifiedSelection);
        setSelectedCounter(selectedCounter + added);
      }
      onChangeStaff(newSelection);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStaff]
  );
  return (
    <Autocomplete
      multiple
      renderInput={(params) => {
        const { InputProps, ...restParams } = params;
        return (
          <TextField
            {...restParams}
            placeholder="Search staff"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            ref={InputProps.ref}
          />
        );
      }}
      options={staff}
      getOptionLabel={(option) => option?.fullName}
      renderTags={() => null}
      disableCloseOnSelect
      getOptionDisabled={(option: StaffMember) => option.id === currentUser.id}
      onChange={(_event, values) => handleChange(values)}
      renderOption={(props, option, { selected }) => {
        const { key, ...restProps } = props;
        return (
          <ListItem
            {...restProps}
            key={option.id}
            secondaryAction={<Checkbox edge="end" checked={selected} />}
          >
            <ListItemText primary={option.fullName} secondary={option.role} />
            <ListItemAvatar sx={{ marginRight: "1rem" }}>
              <Avatar alt={option.fullName} src={option.avatar} />
            </ListItemAvatar>
          </ListItem>
        );
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={Array.from(selectedStaff.values())}
    />
  );
}
